import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { hydratedGlobalParams, normalizeFilterParams } from '../../../shared/workspace-element-context';

@Injectable({
  providedIn: 'root'
})
export class PartnerAllocService {

  constructor(
    private http: HttpClient,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  private getGlobalParams(): any {
    return hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
  }

  private getFilterParams(): any {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    return normalizeFilterParams(activeScreen);
  }

  private getUrlEncodedHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
  }

  private toUrlEncoded(params: any): string {
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach((key: string) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });
    return urlEncoded.toString();
  }

  private cleanParams(params: any): any {
    const cleaned: any = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        cleaned[key] = params[key];
      }
    });
    return cleaned;
  }

  private getScenarioMeta(mergedParams: any): { scenarioCode: string; is_issue_key_enabled: string } {
    const result = { scenarioCode: '', is_issue_key_enabled: '' };
    try {
      const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
      if (activeScreen?.filters && typeof activeScreen.filters.getFilters === 'function') {
        const foundScenario = _.find(activeScreen.filters.getFilters(), { param_name: 'scenario' });
        if (foundScenario?.selected) {
          result.scenarioCode = foundScenario.selected.CODE || '';
          result.is_issue_key_enabled = foundScenario.selected.ISSUE_ID_ENABLED || '';
          return result;
        }
      }
      if (mergedParams.scenario && mergedParams.tax_year) {
        const inputs = this.gtwGlobal.global?.inputs;
        const taxYearEntry = _.find(inputs?.tax_year?.values, (i: any) => i.value == mergedParams.tax_year);
        if (taxYearEntry) {
          const foundScenario = _.find(taxYearEntry.scenarios, { scenario: mergedParams.scenario });
          if (foundScenario) {
            result.scenarioCode = foundScenario.scenario_type_code || '';
            result.is_issue_key_enabled = foundScenario.is_issue_key_enabled || '';
          }
        }
      }
    } catch (e) {}
    return result;
  }

  private loadJsonObject(baseUrl: string, params: any): Observable<any> {
    const globalParams = this.getGlobalParams();
    const filterParams = this.getFilterParams();
    const mergedParams = _.merge({}, globalParams, filterParams, this.cleanParams(params));
    delete mergedParams.checkLockedbj;

    // Inject scenarioCode + is_issue_key_enabled exactly as JsonObjectFactory.getJSONObj does
    const scenarioMeta = this.getScenarioMeta(mergedParams);
    if (scenarioMeta.scenarioCode) { mergedParams.scenarioCode = scenarioMeta.scenarioCode; }
    if (scenarioMeta.is_issue_key_enabled) { mergedParams.is_issue_key_enabled = scenarioMeta.is_issue_key_enabled; }

    return this.http.get<any>(`${baseUrl}/loadJsonObject`, {
      params: mergedParams,
      withCredentials: true
    }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1') {
          return response;
        }
        throw new Error(response.errorMessage || 'Failed to load data');
      }),
      catchError((error) => throwError(error))
    );
  }

  // Load K1 allocation form details (action_code: ljeotj)
  loadK1FormDetails(baseUrl: string, taxYear: string, scenario: string, rowData: any, ssoId: string): Observable<any> {
    const params: any = {
      action_code: 'ljeotj',
      tax_year: taxYear,
      scenario: scenario,
      section_name: '',
      leid: rowData.PSHIP_LEID,
      cdr_no: rowData.PSHIP_TAX_CODE,
      rp: rowData.PSHIP_REPORTING_PERIOD,
      bsla: rowData.PSHIP_BUSINESS,
      curr: rowData.PSHIP_CURRENCY,
      ptr_leid: rowData.POSTING_PARTNER_LEID,
      ptr_cdr_no: rowData.POSTING_PARTNER_TAX_CODE,
      ptr_rp: rowData.POSTING_PARTNER_REPORTING_PERIOD,
      ptr_bsla: rowData.POSTING_PARTNER_BUSINESS,
      ptr_curr: rowData.POSTING_PARTNER_CURRENCY,
      sso_id: ssoId,
      cc_key: rowData.COMBINATION_KEY,
      ptr_cc_key: rowData.PTR_COMBINATION_KEY
    };
    return this.loadJsonObject(baseUrl, params);
  }

  // Load FSI form details (action_code: xuhds1)
  loadFSIFormDetails(baseUrl: string, taxYear: string, scenario: string, rowData: any, ssoId: string): Observable<any> {
    const params: any = {
      action_code: 'xuhds1',
      section_name: 'FSI',
      tax_year: taxYear,
      scenario: scenario,
      leid: rowData.PSHIP_LEID,
      cdr_no: rowData.PSHIP_TAX_CODE,
      rp: rowData.PSHIP_REPORTING_PERIOD,
      bsla: rowData.PSHIP_BUSINESS,
      curr: rowData.PSHIP_CURRENCY,
      ptr_leid: rowData.POSTING_PARTNER_LEID,
      ptr_cdr_no: rowData.POSTING_PARTNER_TAX_CODE,
      ptr_rp: rowData.POSTING_PARTNER_REPORTING_PERIOD,
      ptr_bsla: rowData.POSTING_PARTNER_BUSINESS,
      ptr_curr: rowData.POSTING_PARTNER_CURRENCY,
      sso_id: ssoId,
      cc_key: rowData.COMBINATION_KEY,
      ptr_cc_key: rowData.PTR_COMBINATION_KEY
    };
    return this.loadJsonObject(baseUrl, params);
  }

  // Load GI accounts with baskets (action_code: rp94bf)
  loadGIAccountsWithBaskets(baseUrl: string, taxYear: string, scenario: string, rowData: any, jcdKey: string, countryKeyList: string, ssoId: string): Observable<any> {
    const params: any = {
      action_code: 'rp94bf',
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: jcdKey,
      cc_key: rowData.COMBINATION_KEY,
      ptr_cc_key: rowData.PTR_COMBINATION_KEY,
      country_keys: countryKeyList,
      sso_id: ssoId
    };
    return this.loadJsonObject(baseUrl, params);
  }

  // Load BEAT/GILTI/FDII data (action_code: g443my)
  loadBeatGiltiFdii(baseUrl: string, taxYear: string, scenario: string, rowData: any, jcdKey: string, countryKeyList: string, ssoId: string): Observable<any> {
    const params: any = {
      action_code: 'g443my',
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: jcdKey,
      cc_key: rowData.COMBINATION_KEY,
      ptr_cc_key: rowData.PTR_COMBINATION_KEY,
      country_keys: countryKeyList,
      sso_id: ssoId
    };
    return this.loadJsonObject(baseUrl, params);
  }

  // Load issue dropdown (action_code: pgfpd1)
  loadIssueDropdown(baseUrl: string, taxYear: string, scenario: string, jcdKey: string): Observable<any> {
    const params: any = {
      action_code: 'pgfpd1',
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: jcdKey
    };
    return this.loadJsonObject(baseUrl, params);
  }

  // Load issue details (action_code: fqrfx9)
  loadIssueDetails(baseUrl: string, taxYear: string, scenario: string, jcdKey: string, combinationKey: string, ptrCombinationKey: string): Observable<any> {
    const params: any = {
      action_code: 'fqrfx9',
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: jcdKey,
      combination_key: combinationKey,
      ptr_combination_key: ptrCombinationKey
    };
    return this.loadJsonObject(baseUrl, params);
  }

  // Load all dropdowns (action_code: oz319t)
  loadAllDropdowns(baseUrl: string, taxYear: string): Observable<any> {
    const params: any = {
      action_code: 'oz319t',
      tax_year: taxYear
    };
    return this.loadJsonObject(baseUrl, params);
  }

  // Load form formula details (action_code: 99t6eb)
  loadFormFormulaDetails(baseUrl: string, taxYear: string, scenario: string): Observable<any> {
    const params: any = {
      action_code: '99t6eb',
      tax_year: taxYear,
      scenario: scenario,
      part_no: '',
      section_name: ''
    };
    return this.loadJsonObject(baseUrl, params);
  }

  // Load country list (action_code: ynqodb)
  loadCountryList(baseUrl: string, taxYear: string, ssoId: string): Observable<any> {
    const params: any = {
      action_code: 'ynqodb',
      tax_year: taxYear,
      sso_id: ssoId
    };
    return this.loadJsonObject(baseUrl, params);
  }

  // Save partner allocation form
  savePartnerAllocationForm(baseUrl: string, clobSettings: any, clobData: any[]): Observable<any> {
    const globalParams = this.getGlobalParams();
    const filterParams = this.getFilterParams();

    let params: any = _.merge({}, globalParams, filterParams);
    params.clob_data = JSON.stringify(clobData);
    params.clob_settings = JSON.stringify(clobSettings);
    params.process_name = 'Partner Allocation Setup';
    params.jcd_key = globalParams.jcd_key || filterParams.jcd_key;
    params.sso_id = clobSettings.sso_id;

    const headers = this.getUrlEncodedHeaders();
    return this.http.post<any>(
      `${baseUrl}/saveJsonObject?action_code=ge4rfd`,
      this.toUrlEncoded(params),
      { headers, withCredentials: true }
    ).pipe(
      map((response: any) => response.data || response),
      catchError((error) => throwError(error))
    );
  }

}
